import Box from "@material-ui/core/Box";
import Divider from "@material-ui/core/Divider";
import Paper, { PaperProps } from "@material-ui/core/Paper";
import { createStyles, makeStyles } from "@material-ui/core/styles";
import MuiTextField, { BaseTextFieldProps } from "@material-ui/core/TextField";
import Autocomplete, { AutocompleteChangeDetails, AutocompleteChangeReason, AutocompleteGetTagProps, AutocompleteProps } from "@material-ui/lab/Autocomplete";
import { Value as InternalValue } from "@material-ui/lab/useAutocomplete";
import clsx from "clsx";
import React, { useCallback } from "react";
import { useFocus } from "../../hook";
import { HasTestId, PlainFunctionComponent } from "../../types";
import { isArray, isDefined, neverReachedFor } from "../../util";
import { TextFieldProps } from "../textField";
import { ComboBoxPaperContext, useComboBoxPaper } from "./context/ComboBoxPaperContext";
import { EndAdornment } from "./EndAdornment";
import { SelectableOption } from "./SelectableOption";
import { VirtualizedListBox } from "./VirtualizedListBox";

type MultiComboBoxChangeReason = AutocompleteChangeReason | "select-all";

export interface MultiComboBoxProps<ValueT>
    extends Omit<
            AutocompleteProps<ValueT, true, true | undefined, false>,
            | "debug" // <- no longer available in v5.0.0 so remove it already now
            | "defaultValue"
            | "forcePopupIcon"
            | "freeSolo"
            | "getLimitTagsText"
            | "limitTags"
            | "multiple"
            | "onChange"
            | "openText"
            | "renderInput"
            | "renderTags"
            | "value"
        >,
        HasTestId {
    /**
     * Reference to the root element of the underlying Material UI Autocomplete component. We don't use ref forwarding for this since using `React.forwardRef` with generic components causes typing problems.
     */
    autocompleteRef?: React.Ref<HTMLDivElement>;
    /**
     * Whether the lock icon should be displayed if the field is `disabled`. Defaults to `undefined` which is interpreted as `false`.
     */
    disableLockIcon?: boolean;
    /**
     * Don't display the validation icon. Defaults to `undefined` which is interpreted as `false` (= validation icon is shown if text is not empty depending on `error` property).
     */
    disableValidationIcon?: boolean;
    error?: BaseTextFieldProps["error"];
    helperText?: BaseTextFieldProps["helperText"];
    label: React.ReactNode;
    /**
     * Callback fired when the value changes.
     *
     * @param event The event source of the callback.
     * @param value The new value of the component.
     * @param reason One of "create-option", "select-option", "remove-option", "blur", "clear", or "select-all" (last is new on this implementation).
     */
    onChange: (value: ValueT[], reason: MultiComboBoxChangeReason, event?: React.ChangeEvent<unknown>, details?: AutocompleteChangeDetails<ValueT> | undefined) => void;
    /**
     * Optional component that will be shown at the end of the options if provided.
     */
    optionsFooter?: React.ReactElement;
    placeholder: TextFieldProps["placeholder"];
    /**
     * Optional function to render the selected options in the dropdown.
     * Renders `getOptionLabel`s as string prefixed with the selected option count by default, e.g. »(2) Option2, Option1« (in selection order).
     * If `"useChips"` is used, renders chips labeled  with `getOptionLabel`.
     *
     * Note: Even when a custom `renderTags` function is provided, the returned `ReactNode` will be wrapped into a `<Box>` applying default styles.
     *       If you want to overwrite them, wrap your component into an own `Box` with custom styles.
     */
    renderTags?: "useChips" | AutocompleteProps<ValueT, true, true | undefined, false>["renderTags"];
    /**
     * If `true`, the label is displayed as required and the `input` element` will be required.
     */
    required?: boolean;
    /**
     * Can be set to a shown `ReactNode` or `false` if it should not be displayed.
     */
    selectAllLabel?: React.ReactNode | false;
    /**
     * Determines whether `Chip`s should be used to show selected items. Default is text, e.g. `(3) Option 2, Option 5, ...` without line breaks.
     * @deprecated since version 3.0 (use `renderTags: "useChips"` instead)
     */
    useChips?: boolean;
    /**
     * Enables the virtualization of the dropdown list box. Use this if you have to render lots of options. Defaults to `false`.
     */
    useListVirtualization?: boolean;
    value: ValueT[];
}

export const MultiComboBox = <ValueT extends unknown>({
    autocompleteRef,
    ChipProps,
    disabled,
    disableClearable,
    disableLockIcon,
    disableValidationIcon,
    useListVirtualization,
    error,
    // defaults to `(option) => option.label ?? option` in Material-UI 5.0, see https://next.material-ui.com/api/autocomplete/
    getOptionLabel = (option: ValueT) =>
        // eslint-disable-next-line @typescript-eslint/ban-types
        typeof option === "object" && "label" in (option as object) ? ((option as unknown) as { label: string }).label : ((option as unknown) as string),
    helperText,
    label,
    onBlur,
    onChange,
    onFocus,
    open,
    openOnFocus = true,
    options,
    optionsFooter,
    PaperComponent = Paper, // same default as in `Autocomplete`
    placeholder,
    required,
    renderTags,
    selectAllLabel,
    testId,
    useChips,
    value,
    ...props
}: MultiComboBoxProps<ValueT>): React.ReactElement<MultiComboBoxProps<ValueT>> => {
    const { focus, handleBlur, handleFocus } = useFocus(false, onBlur, onFocus);
    const classes = useStyles();

    if (isDefined(useChips) && process.env.NODE_ENV !== "production") {
        console.warn("[MultiComboBox] using `useChips` is deprecated and will be removed in the future. Please use `renderTags` instead.");
        renderTags = useChips ? "useChips" : renderTags;
    }

    const handleChange = useCallback(
        (
            event: React.ChangeEvent<unknown>,
            value: InternalValue<ValueT, true, true | undefined, false>,
            reason: AutocompleteChangeReason,
            details?: AutocompleteChangeDetails<ValueT> | undefined,
        ) => {
            return onChange(value, reason, event, details);
        },
        [onChange],
    );

    const handleSelectAll = useCallback(
        (action: "clearAll" | "selectAll"): void =>
            action === "clearAll" ? onChange([], "clear") : action === "selectAll" ? onChange(options, "select-all") : neverReachedFor(action),
        [onChange, options],
    );

    const defaultRenderTags = useCallback(
        (values: ValueT[]) => (
            <Box className={clsx({ [classes.inputText]: !disabled })}>{values.length > 0 ? `(${values.length}) ${values.map(getOptionLabel).join(", ")}` : ""}</Box>
        ),
        [getOptionLabel, disabled],
    );

    const renderTagsFunction = useCallback(
        (values: ValueT[], getTagProps: AutocompleteGetTagProps) => (
            <Box className={classes.inputTextSpace}>{((renderTags === "useChips" ? () => null : renderTags) ?? defaultRenderTags)(values, getTagProps)}</Box>
        ),
        [renderTags, defaultRenderTags],
    );

    return (
        <ComboBoxPaperContext.Provider
            value={{
                onSelectAll: handleSelectAll,
                optionsFooter,
                PaperComponent,
                selectAllLabel,
                selectAllState: options.length === value.length ? "checked" : value.length > 0 ? "indeterminate" : "unchecked",
            }}
        >
            <Autocomplete<ValueT, true, true | undefined, false>
                ChipProps={{ ...ChipProps, variant: "outlined" }}
                clearOnBlur={false}
                data-testid={testId}
                disableClearable={disabled || disableClearable || !focus || !value || (isArray(value) && value.length === 0) ? true : undefined}
                disableCloseOnSelect={true}
                disabled={disabled}
                forcePopupIcon={!disabled}
                getOptionLabel={getOptionLabel}
                ListboxComponent={useListVirtualization ? VirtualizedListBox : undefined}
                multiple={true}
                onBlur={handleBlur}
                onChange={handleChange}
                onFocus={handleFocus}
                open={open}
                openOnFocus={openOnFocus}
                options={options}
                PaperComponent={MultiComboBoxPaper}
                ref={autocompleteRef}
                renderInput={({ InputProps, ...params }) => (
                    <MuiTextField
                        {...params}
                        error={error}
                        helperText={helperText || <span />} // at least a tag prevents helper text area from shrinking...
                        InputProps={{
                            ...InputProps,
                            className: clsx(InputProps?.className, { required }),
                            endAdornment: (
                                <EndAdornment
                                    disabled={disabled}
                                    disableClearButton={disableClearable}
                                    disableLockIcon={disableLockIcon}
                                    disableValidationIcon={disableValidationIcon}
                                    error={error}
                                    focus={focus}
                                    value={value}
                                >
                                    {InputProps.endAdornment}
                                </EndAdornment>
                            ),
                        }}
                        label={label}
                        placeholder={value.length === 0 ? placeholder : undefined} // TODO:FMe or always show?
                        required={required}
                        variant="filled"
                    />
                )}
                renderOption={(value, { selected }) => {
                    const label = getOptionLabel(value);
                    return <SelectableOption key={label} label={label} selected={selected ? "checked" : "unchecked"} />;
                }}
                renderTags={renderTags === "useChips" ? undefined : renderTagsFunction}
                // default to `[]` because otherwise input thinks to be uncontrolled on `undefined` which causes a thrown exception on console by React
                value={value ?? []}
                {...props}
            />
        </ComboBoxPaperContext.Provider>
    );
};

const MultiComboBoxPaper: PlainFunctionComponent<PaperProps> = React.forwardRef(({ children, ...props }: PaperProps, ref) => {
    const { onSelectAll, optionsFooter, PaperComponent, selectAllLabel, selectAllState } = useComboBoxPaper();

    const handleSelectAll = useCallback(() => {
        switch (selectAllState) {
            case "checked":
                return onSelectAll("clearAll");
            case "indeterminate":
            case "unchecked":
                return onSelectAll("selectAll");
            default:
                return neverReachedFor(selectAllState);
        }
    }, [onSelectAll, selectAllState]);

    const preventBlur = useCallback((event: React.MouseEvent) => event.preventDefault(), []);

    const classes = useStyles();
    return (
        <PaperComponent ref={ref} {...props}>
            {selectAllLabel && (
                <>
                    <SelectableOption
                        className={classes.selectAll}
                        label={selectAllLabel}
                        onClick={handleSelectAll}
                        onMouseDown={preventBlur} // otherwise click does causes popup to be closed
                        selected={selectAllState}
                    />
                    <Divider variant="middle" />
                </>
            )}
            {children}
            {optionsFooter && (
                <>
                    <Divider variant="middle" />
                    <Box className={classes.optionBox}>{optionsFooter}</Box>
                </>
            )}
        </PaperComponent>
    );
});

const useStyles = makeStyles(
    createStyles({
        inputText: {
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
        },
        inputTextSpace: {
            maxWidth: "90%", // keep remaining `10%` for the "input field" or better the input cursor (preserve filter capabilities and don't show empty next line with "input")
            padding: "5.5px 4px", // take over `padding` from `MuiAutocomplete` for `input` as it is "floated away" on multiline text...
        },
        optionBox: {
            padding: "8px 16px",
        },
        selectAll: {
            cursor: "pointer",
            display: "flex", // makes component grow and fills complete line
            padding: "8px 16px",
        },
    }),
);
